import { createTheme } from '@mui/material/styles';
import { color, radius, radiusPx, motion, r, FONT_FAMILY, tabularNums } from './theme/tokens';

// ─── The MUI theme ────────────────────────────────────────────────────────────
//
// The palette below reads from `src/theme/tokens.js`, which is the single
// source of design values in this frontend and is locked by
// `Brand_Design/mystack-design-system-brief-Aug.html`. Do not add a hex to this
// file — add it to the token module, or (more likely) use one already there.
//
// Everything under "Deprecated" further down predates the token module and is
// kept only so the surfaces still importing it keep rendering. Those aliases
// retire surface-by-surface in the later phases of
// `docs/design-system-compliance/DESIGN_DOC.md` §7.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Deprecated: pre-brief green family ───────────────────────────────────────
// Retired by Phase Sweep — do not use in new code. For the surfaces still
// importing it, ask the tree rather than trusting a list written here:
//   grep -rn "import {[^}]*green" src --include='*.js'
const green = {
  brand:   '#2E7D32',   // MUI green[800] — main brand green
  cta:     '#1B4332',   // Deep forest — CTA card gradient start
  ctaDark: '#0A2518',   // Near-black forest — CTA card gradient end
  tintSm:  'rgba(46, 125, 50, 0.05)',
  tintMd:  'rgba(46, 125, 50, 0.14)',
  shadow:  'rgba(46, 125, 50, 0.18)',
};

// ─── Deprecated: pre-brief neutral ramp ───────────────────────────────────────
// Retired by Phase Sweep alongside `green` — do not use in new code.
const neutral = {
  900: '#1A202C',  // Primary text
  700: '#4A5568',  // Secondary text
  500: '#718096',  // Muted / captions
  300: '#CBD5E0',  // Borders on white
  100: '#F7FAFC',  // Page / card background
  50:  '#FFFFFF',
};

const theme = createTheme({
  // Palette — brief §3. Two greens do the work, and the signal accent is the
  // only action colour in the product. `primary.dark` is deliberately left for
  // MUI to derive: the brief locks no darker evergreen, and `color.heroDark` is
  // a hero gradient endpoint that must never become a standalone surface.
  palette: {
    primary: {
      main:  color.evergreen,  // structure: AppBar, filled buttons, brand identity
      light: color.accent,     // the one action colour: CTAs, links, focus rings
      contrastText: '#FFFFFF',
    },
    secondary: {
      // Was amber — a risk colour used decoratively, which the brief forbids.
      // There is no second action colour, so secondary is the accent with the
      // dark ink the brief mandates on accent fills.
      main: color.accent,
      contrastText: color.accentInk,
    },
    // Semantic entries. Without these, MUI's defaults apply — including a blue
    // `info` (#0288d1), which no grep for hex literals would ever have found.
    error:   { main: color.riskHigh },
    warning: { main: color.riskMedium },
    info:    { main: color.evergreen },  // never blue; informational == structural
    success: { main: color.accent, contrastText: color.accentInk },
    text: {
      primary:   color.ink,
      secondary: color.inkSoft,
      disabled:  color.inkMuted,
    },
    background: {
      default: color.surface,  // page ground
      paper:   color.paper,    // cards sit a shade lighter than the page
    },
    divider: color.line,
  },

  // Deprecated: pre-brief surface tints, still read by ~21 call sites. Better
  // than an ad-hoc `alpha(primary.main, x)`, but not brief values — Phase Sweep
  // retires them with the surfaces that use them.
  surfaces: {
    brandTintSm: green.tintSm,
    brandTintMd: green.tintMd,
    brandShadow: green.shadow,
    ctaGradient: `linear-gradient(135deg, ${green.cta} 0%, ${green.ctaDark} 100%)`,
  },

  // `gradients.primary` / `.hover` used to live here — a blue-to-green button
  // fill the brief's one-action-colour rule retires. Deleted, along with their
  // call sites, rather than carried as deprecated aliases: the primary button
  // is now a flat `primary.main` fill. Phase Controls turns that into a proper
  // theme variant.
  customShadows: {
    button:      `0 0.125rem 0.5rem ${green.shadow}`,
    buttonHover: `0 0.25rem 0.9375rem rgba(46, 125, 50, 0.35)`,
  },

  typography: {
    // Archivo, from the token module. The stack this replaced named Inter
    // first — a face the app never actually loaded, so every surface silently
    // fell through to Roboto.
    fontFamily: FONT_FAMILY,
    h1: { fontSize: '2.5rem',   fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.15 },
    h2: { fontSize: '2rem',     fontWeight: 700, letterSpacing: '-0.025em' },
    h3: { fontSize: '1.625rem', fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontSize: '1.375rem', fontWeight: 700 },
    h5: { fontSize: '1.125rem', fontWeight: 700 },
    h6: { fontSize: '1rem',     fontWeight: 700 },
    body1: { fontSize: '1rem',    lineHeight: 1.7 },
    body2: { fontSize: '0.875rem', lineHeight: 1.65 },
    caption: { fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.4 },
    overline: {
      fontSize: '0.7rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      lineHeight: 1.5,
    },
    subtitle1: { fontSize: '1rem',    fontWeight: 600, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.45 },

    // Numeric readouts — doses, scores, study counts, p-values. Carries
    // tabular figures so digits share one advance width and columns of numbers
    // align. Deliberately inherits size and weight from context rather than
    // fixing them: the same variant has to sit in a table cell, a gauge and a
    // caption. Rendered as a <span> (see MuiTypography.variantMapping) so it
    // can wrap a number inside a sentence.
    data: { ...tabularNums },
  },

  shape: {
    borderRadius: radiusPx.md,  // the brief locks 8 / 12 / 16; 12 is the card default
  },

  components: {
    // ─── Global focus ring — brief §9 ─────────────────────────────────────────
    // "Visible, 3:1 contrast, 2px solid outline, 2px offset, signal accent.
    // Required on all interactive elements." Written in rem per the frontend
    // rule; `r(2)` is the token module's px→rem helper, so no px literal
    // enters this file.
    //
    // `:focus-visible` rather than `:focus` — a mouse click must not paint a
    // ring, while keyboard navigation always must. `.Mui-focusVisible` gets the
    // same treatment because MUI's ButtonBase tracks focus itself and applies
    // that class where the native pseudo-class does not always match.
    //
    // The `html` prefix is load-bearing, not decoration. Three legacy
    // stylesheets kill the browser default with a bare `outline: none` on the
    // element's base rule — `Home.css`, `auth/GoogleButton.css` and
    // `auth/AppleButton.css`, all written before `:focus-visible` existed.
    // `*:focus-visible` alone ties those class selectors on specificity, so
    // which one wins comes down to stylesheet order; `html *:focus-visible`
    // outranks them outright and the ring is genuinely global. (AppleButton
    // already restores its own ring locally; the other two do not, so without
    // this the login page would tab with no visible focus at all.)
    MuiCssBaseline: {
      styleOverrides: {
        'html *:focus-visible, html .Mui-focusVisible': {
          outline: `${r(2)} solid ${color.accent}`,
          outlineOffset: r(2),
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        // Without an entry here MUI has no element to render `variant="data"`
        // as and falls back to <span> with a console warning in dev. Declaring
        // it makes the mapping deliberate.
        variantMapping: { data: 'span' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          textTransform: 'none',
          fontWeight: 600,
          '&.Mui-disabled': {
            background: `${neutral[500]} !important`,
            color: '#fff !important',
            boxShadow: 'none !important',
          },
        },
        // containedPrimary intentionally omitted — palette.primary.main provides
        // the default contained-primary fill, and buttonStyles.primary sets the
        // same fill explicitly (it is applied to buttons with no variant too).
      },
      // ─── The brief's three buttons — §6 ────────────────────────────────────
      // "One primary per view. Ghost for evidence navigation. No blue, no teal
      // gradient." Dark ink on the accent fill is deliberate (brief §10): it is
      // what makes the control read as engineered software rather than generic
      // wellness, and white on accent fails AA at body size.
      //
      // These are opt-in: a surface reaches them with `variant="primary|
      // secondary|ghost"`. Existing `contained` / `outlined` buttons are
      // deliberately left alone — repointing them app-wide is per-surface
      // colour work, which Phase Sweep owns.
      variants: [
        {
          props: { variant: 'primary' },
          style: {
            backgroundColor: color.accent,
            color: color.accentInk,
            '&:hover': { backgroundColor: color.accentHover },
          },
        },
        {
          props: { variant: 'secondary' },
          style: {
            backgroundColor: 'transparent',
            color: color.ink,
            border: `${r(1)} solid ${color.line}`,
            '&:hover': {
              borderColor: color.evergreen,
              backgroundColor: color.surface,
            },
          },
        },
        {
          props: { variant: 'ghost' },
          style: {
            backgroundColor: 'transparent',
            color: color.accent,
            '&:hover': {
              backgroundColor: 'transparent',
              color: color.linkHover,
            },
          },
        },
      ],
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.sm },
      },
    },
    // `MuiPaper` keeps its radius but NOT a shadow reset. Paper is the base of
    // Dialog, Menu and Snackbar, so neutralizing shadow here would strip it
    // from exactly the floating elements the brief reserves it for. The reset
    // belongs on Card — see below.
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: radius.lg },
      },
    },
    // ─── Cards — brief §6 ─────────────────────────────────────────────────────
    // The brief specifies a hairline border in `color.line` on a `color.paper`
    // ground at the large radius, and is explicit that lists carry no
    // box-shadow — hairlines do the structural work, and shadow is reserved for
    // floating elements. `elevation: 0` as a default prop stops MUI emitting an
    // elevation shadow class at all, rather than relying on this override
    // out-specifying one.
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          backgroundColor: color.paper,
          border: `${r(1)} solid ${color.line}`,
          boxShadow: 'none',
        },
      },
      // The brief's hover rule (border-color darkens to the evergreen) is an
      // affordance for cards you can actually click, so it ships as an opt-in
      // variant rather than on every Card. A static info card that reacts to
      // the pointer promises an interaction it does not have.
      variants: [
        {
          props: { variant: 'interactive' },
          style: {
            transition: `border-color ${motion.micro} ${motion.ease}`,
            '&:hover': { borderColor: color.evergreen },
          },
        },
      ],
    },
    MuiAppBar: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },
  },
});

// Deprecated named exports. New code imports from `src/theme/tokens.js`;
// these survive for the surfaces Phase Sweep has not reached yet.
export { green, neutral };

// Deprecated alias. Kept for one phase per design doc D-9, then removed.
// For the surfaces still importing it:
//   grep -rn "import {[^}]*colors" src --include='*.js'
export const colors = {
  green: {
    main:        '#185002',
    light:       green.brand,  // #2E7D32
    medium:      green.brand,
    dark:        green.cta,
    hover:       green.ctaDark,
    primaryCta:  green.cta,
    transparent: {
      light: green.tintSm,
      medium: green.tintMd,
    },
    shadow: green.shadow,
  },
  grey: {
    border:     neutral[300],
    background: neutral[100],
    text:       neutral[500],
    dark:       neutral[900],
    disabled:   '#A0AEC0',
  },
};

export default theme;
